<script setup>
import { computed } from 'vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';
import {
  formatChannelType,
  channelTypeIcon,
} from '../../helpers/kraftchatAIFormatters';

const props = defineProps({
  inboxes: {
    type: Array,
    default: () => [],
  },
  selectedInboxId: {
    type: [Number, String],
    default: null,
  },
});

const emit = defineEmits(['select', 'configureRules']);

const content = {
  title: 'Caixas de entrada',
  description:
    'Escolha em qual caixa de entrada a IA deve responder automaticamente.',
  emptyLabel: 'Nenhuma caixa de entrada disponível para atendimento com IA.',
  blockedLabel: 'Bloqueado pelo plano atual',
  activeLabel: 'Resposta automática ativa',
  inactiveLabel: 'Resposta automática inativa',
  notConfiguredLabel: 'Sem agente configurado',
  notReadyLabel: 'Configuração incompleta',
  needsRulesLabel: 'Regras pendentes',
  configureLabel: 'Configurar',
  editLabel: 'Editar',
  defineRulesLabel: 'Definir regras',
};

// Readiness failing_checks that only "Regras e ações" (the agent's policy)
// can fix - see Kraftchat::Ai::Agents::ReadinessPolicy::CONTEXTS[:operational].
// When every failing check belongs to this set, sending the admin back to
// "Editar" (the agent form) would show a form with nothing left to fix -
// the missing piece lives on a different screen entirely.
const RULES_ONLY_CHECKS = ['policy_present', 'auto_reply_enabled'];

const isSelected = inbox => String(inbox.id) === String(props.selectedInboxId);

const needsRulesOnly = inbox => {
  const failingChecks = inbox.readiness?.failing_checks || [];
  return (
    failingChecks.length > 0 &&
    failingChecks.every(check => RULES_ONLY_CHECKS.includes(check))
  );
};

const statusFor = inbox => {
  if (!inbox.ai_allowed) return { label: content.blockedLabel, tone: 'ruby' };
  if (!inbox.agent_id)
    return { label: content.notConfiguredLabel, tone: 'slate' };
  if (!inbox.readiness?.ready) {
    return needsRulesOnly(inbox)
      ? { label: content.needsRulesLabel, tone: 'amber' }
      : { label: content.notReadyLabel, tone: 'amber' };
  }
  if (inbox.auto_reply_enabled)
    return { label: content.activeLabel, tone: 'green' };

  return { label: content.inactiveLabel, tone: 'slate' };
};

const toneClasses = {
  ruby: 'bg-n-ruby-3 text-n-ruby-11',
  amber: 'bg-n-amber-3 text-n-amber-11',
  green: 'bg-n-teal-3 text-n-teal-11',
  slate: 'bg-n-alpha-2 text-n-slate-11',
};

const actionLabelFor = (inbox, rulesOnly) => {
  if (rulesOnly) return content.defineRulesLabel;
  if (inbox.agent_id) return content.editLabel;
  return content.configureLabel;
};

const rows = computed(() =>
  props.inboxes.map(inbox => {
    const rulesOnly = Boolean(inbox.agent_id && needsRulesOnly(inbox));

    return {
      inbox,
      status: statusFor(inbox),
      needsRulesOnly: rulesOnly,
      actionLabel: actionLabelFor(inbox, rulesOnly),
    };
  })
);

const handleAction = row => {
  if (row.needsRulesOnly) {
    emit('configureRules', row.inbox);
    return;
  }

  emit('select', row.inbox);
};
</script>

<template>
  <section class="rounded-xl border border-n-weak bg-n-solid-1 p-6 shadow-sm">
    <div class="space-y-1 mb-4">
      <h2 class="text-lg font-semibold text-n-slate-12">
        {{ content.title }}
      </h2>
      <p class="text-sm leading-6 text-n-slate-11">
        {{ content.description }}
      </p>
    </div>

    <p v-if="!rows.length" class="text-sm text-n-slate-11">
      {{ content.emptyLabel }}
    </p>

    <ul v-else class="space-y-2">
      <li
        v-for="row in rows"
        :key="row.inbox.id"
        class="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3"
        :class="
          isSelected(row.inbox)
            ? 'border-n-brand bg-n-brand/5'
            : 'border-n-weak bg-n-background'
        "
      >
        <div class="flex min-w-0 items-center gap-3">
          <div
            class="grid size-9 flex-shrink-0 place-content-center rounded-lg bg-n-alpha-1 text-n-slate-11"
          >
            <Icon
              :icon="channelTypeIcon(row.inbox.channel_type)"
              class="size-5"
            />
          </div>

          <div class="min-w-0">
            <p class="truncate text-sm font-medium text-n-slate-12">
              {{ row.inbox.name }}
            </p>
            <p class="text-xs text-n-slate-11">
              {{ formatChannelType(row.inbox.channel_type) }}
            </p>
          </div>
        </div>

        <div class="flex items-center gap-3">
          <span
            class="rounded-md px-2 py-0.5 text-xs font-medium"
            :class="toneClasses[row.status.tone]"
          >
            {{ row.status.label }}
          </span>

          <button
            v-if="row.needsRulesOnly"
            type="button"
            class="text-sm font-medium text-n-brand underline-offset-2 hover:underline"
            @click="handleAction(row)"
          >
            {{ row.actionLabel }}
          </button>

          <NextButton
            v-else
            type="button"
            sm
            :outline="!isSelected(row.inbox)"
            :label="row.actionLabel"
            :disabled="!row.inbox.ai_allowed && !row.inbox.agent_id"
            @click="handleAction(row)"
          />
        </div>
      </li>
    </ul>
  </section>
</template>
